<script setup>
/**
 * The trips of the customer this conversation is with.
 *
 * Asked for the CONTACT, never the conversation. A trip belongs to the customer and its
 * conversation is nullable - a customer who comes back two months later to change a flight
 * must reach the trip they already have rather than start a duplicate with no locator. See
 * docs/travelport/trip-and-conversation-design.md.
 *
 * Narrowed to THIS conversation's trips, plus any the customer has that no conversation
 * has claimed. The sidebar answers "what is this thread about"; the customer's full
 * cross-conversation history lives on the contact page, and reproducing it here would make
 * a narrow column carry a timeline it cannot show well.
 *
 * `attention` is emitted upward so the accordion header can carry a dot while the panel is
 * collapsed. The alternative - a badge component doing its own fetch - would ask the same
 * question twice on every conversation open.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTripActions } from 'dashboard/composables/useTripActions';
import TravelTripsAPI from 'dashboard/api/travel/trips';
import TripCard from './TripCard.vue';
import StartTripButton from 'dashboard/components-next/Contacts/StartTripButton.vue';
import TripDetailsDialog from 'dashboard/components-next/Travel/TripDetailsDialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [Number, String], default: null },
  conversationId: { type: [Number, String], default: null },
});

const emit = defineEmits(['attention']);

const { t } = useI18n();

const { refreshingId, acknowledgingId, refresh, acknowledge } =
  useTripActions();

const trips = ref([]);
const isLoading = ref(false);
const openTripId = ref(null);

// Finished journeys are not what this thread is about, and each card costs real height in a
// 320px column. They stay on the contact page, which owns the customer's whole history and
// has the width to show it.
const visible = computed(() =>
  trips.value.filter(trip => trip.status !== 'closed')
);

// Only the two an agent must not miss. Provider staleness is on the card but deliberately
// not badged: the hourly sweep means most sold trips drift stale between refreshes, and a
// dot that is always lit is one nobody reads.
const attention = computed(() => {
  if (trips.value.some(trip => trip.schedule_changed)) return 'alert';
  if (
    trips.value.some(trip => ['expired', 'expiring'].includes(trip.hold?.state))
  ) {
    return 'warning';
  }
  return null;
});

watch(attention, value => emit('attention', value), { immediate: true });

const fetchTrips = async () => {
  if (!props.contactId) {
    trips.value = [];
    return;
  }

  isLoading.value = true;
  try {
    const { data } = await TravelTripsAPI.get({
      contactId: props.contactId,
      conversationId: props.conversationId,
    });
    trips.value = data.trips || [];
  } catch (error) {
    // Non-critical: the panel stays empty rather than shouting at an agent who came here
    // to answer a customer about something else. Actions surface their own errors.
    trips.value = [];
  } finally {
    isLoading.value = false;
  }
};

const replaceTrip = updated => {
  if (!updated) return;
  trips.value = trips.value.map(trip =>
    trip.id === updated.id ? { ...trip, ...updated } : trip
  );
};

const onRefresh = async trip => replaceTrip(await refresh(trip));
const onAcknowledge = async trip => replaceTrip(await acknowledge(trip));

watch(() => [props.contactId, props.conversationId], fetchTrips, {
  immediate: true,
});
</script>

<template>
  <div class="px-4">
    <Spinner v-if="isLoading" class="mx-auto" />
    <template v-else>
      <p v-if="!visible.length" class="text-sm text-n-slate-11">
        {{ t('TRAVEL.TRIP_PANEL.EMPTY') }}
      </p>
      <div v-else class="flex flex-col gap-2">
        <TripCard
          v-for="trip in visible"
          :key="trip.id"
          :trip="trip"
          :is-refreshing="refreshingId === trip.id"
          :is-acknowledging="acknowledgingId === trip.id"
          @refresh="onRefresh"
          @acknowledge="onAcknowledge"
          @open="openTripId = trip.id"
        />
      </div>
      <!-- The way to create a record lives with the record, so the panel is never a dead
           end. It captures a brief and sends the customer nothing, which is why it is here
           rather than in the composer beside Send flight options. -->
      <StartTripButton
        v-if="contactId"
        class="mt-2"
        :contact-id="contactId"
        :conversation-id="conversationId"
        @created="fetchTrips"
      />
    </template>

    <!-- Keyed on the trip so reopening a different one refetches rather than showing the
         previous trip's log while the new one loads. -->
    <TripDetailsDialog
      v-if="openTripId"
      :key="openTripId"
      :trip-id="openTripId"
      @updated="replaceTrip"
      @close="openTripId = null"
    />
  </div>
</template>
